"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
const japaneseMenuItems = [{
  label: "事務所概要",
  href: "#about"
}, {
  label: "取扱業務",
  href: "#services"
}, {
  label: "弁護士・スタッフ",
  href: "#staff"
}, {
  label: "ご相談の流れ",
  href: "#process"
}, {
  label: "English",
  href: "/"
}, {
  label: "お問い合わせ",
  href: "#contact"
}];
function DesktopMenu() {
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-3 lg:gap-8">
        {japaneseMenuItems.map((item, i) => <NavigationMenuItem key={item.label} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href} className="group relative inline-flex h-auto bg-transparent px-0 py-1 font-default text-[11px] font-medium tracking-[0.08em] text-foreground transition-colors duration-200 hover:bg-transparent focus:bg-transparent">
                <span className="relative whitespace-nowrap">
                  {item.label}

                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  return <nav className="flex flex-col gap-1 px-6 pt-12">
      {japaneseMenuItems.map((item, i) => <SheetClose asChild key={item.label} data-index={i}>
          <Link to={item.href} className="group flex items-baseline gap-4 border-b border-border py-5 transition-colors duration-200 hover:text-secondary">
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              {String(i + 1).padStart(2, "0")}
            </span>

            <span className="font-default text-2xl font-light text-foreground transition-colors duration-200 group-hover:text-secondary">
              {item.label}
            </span>
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Mainheader() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      if (!(e.target instanceof Element)) return;
      const anchor = e.target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("#") || href === "#") return;
      const element = document.getElementById(href.slice(1));
      if (!element) return;
      e.preventDefault();
      const header = document.getElementById("mainheader");
      const headerHeight = header?.getBoundingClientRect().height ?? 64;
      const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: reducedMotion ? "auto" : "smooth"
      });
    };
    document.addEventListener("click", handleAnchorClick);
    return () => {
      document.removeEventListener("click", handleAnchorClick);
    };
  }, []);
  return <section data-section-id="3697" id="mainheader" className={`sticky left-0 right-0 top-0 z-50 w-full text-foreground transition-all duration-300 ${scrolled ? "border-b border-border/50 bg-background/90 shadow-md backdrop-blur-md" : "border-b border-border bg-background"}`}>
      <div className="w-full">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-6 px-5 md:px-8 lg:grid lg:grid-cols-[1fr_auto_1fr]">
          {/* Logo */}
          <div className="flex items-center justify-start">
            <Link to="/japanese-language-support/" className="group flex flex-col leading-none">
              <span className="flex items-center">
                <WvcLogo className="h-8 w-auto text-primary" />
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden items-center justify-center lg:flex">
            <DesktopMenu />
          </div>

          {/* Right Side */}
          <div className="flex items-center justify-end gap-5">
            {/* Desktop CTA */}
            <Button asChild size="lg" className="hidden h-11 rounded-md bg-primary px-6 font-default text-[12px] font-semibold tracking-[0.1em] text-primary-foreground shadow-sm hover:bg-primary/90 md:inline-flex">
              <Link to="#contact">お問い合わせ</Link>
            </Button>

            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 rounded-md border border-border text-foreground hover:bg-accent hover:text-accent-foreground lg:hidden" aria-label="メニューを開く">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>

              <SheetContent side="right" className="w-full border-l border-border bg-background p-0 sm:max-w-md">
                <SheetTitle className="sr-only">
                  ナビゲーションメニュー
                </SheetTitle>

                <div className="flex h-full flex-col overflow-y-auto">
                  {/* Mobile Logo */}
                  <div className="flex items-center justify-between border-b border-border px-6 py-6">
                    <SheetClose asChild>
                      <Link to="/japanese-language-support/" className="flex flex-col leading-none">
                        <WvcLogo className="h-7 w-auto text-primary" />
                      </Link>
                    </SheetClose>
                  </div>

                  {/* Mobile Navigation */}
                  <MobileMenu />

                  {/* Mobile CTA */}
                  <div className="mt-auto border-t border-border px-6 py-6">
                    <SheetClose asChild>
                      <Button asChild size="lg" className="h-12 w-full rounded-md bg-primary font-default text-[12px] font-semibold tracking-[0.1em] text-primary-foreground shadow-md hover:bg-primary/90">
                        <Link to="#contact">日本語で問い合わせる</Link>
                      </Button>
                    </SheetClose>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-mono text-[10px] font-medium tracking-[0.2em] text-muted-foreground">
                        法律事務所
                      </span>

                      <span className="font-mono text-[10px] font-medium tracking-[0.2em] text-muted-foreground">
                        南フロリダ
                      </span>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}